import { useState, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { enUS } from 'date-fns/locale';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../Navbar2';
import './index.css';

const ImageSlider = () => {
  const navigate = useNavigate();
  const [villas, setVillas] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState('');
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [maxGuests, setMaxGuests] = useState(1);
  const [blockedDates, setBlockedDates] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const today = new Date();

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch villas from Firestore
  useEffect(() => {
    const fetchVillas = async () => {
      try {
        const villasCollection = collection(db, 'villas');
        const villasSnapshot = await getDocs(villasCollection);
        const villasList = villasSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVillas(villasList);
      } catch (error) {
        console.error('Error fetching villas:', error);
      }
    };
    fetchVillas();
  }, []);

  // Update maxGuests and blockedDates when a villa is selected
  useEffect(() => {
    if (selectedVilla) {
      const villa = villas.find((v) => v.title === selectedVilla);
      if (villa) {
        const newMaxGuests = parseInt(villa.maxGuests) || 1;
        setMaxGuests(newMaxGuests);
        setBlockedDates(villa.blockedDates || []);
        if (adults + children > newMaxGuests) {
          setAdults(Math.min(adults, newMaxGuests));
          setChildren(Math.max(0, newMaxGuests - adults));
          toast.error(`Maximum ${newMaxGuests} guests allowed for this villa`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } else {
        setMaxGuests(1);
        setBlockedDates([]);
      }
    }
  }, [selectedVilla, villas, adults, children]);

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % villas.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [villas.length]);

  // Handle click outside date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.date-picker-wrapper') && !event.target.closest('.rdrDateRangePickerWrapper')) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle date range change with minimum 2 nights
  const handleDateChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    const minEndDate = new Date(startDate);
    minEndDate.setDate(startDate.getDate() + 2);
    
    const selectedNights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (selectedNights < 2) {
      toast.error('Minimum stay of 2 nights required', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    
    setDateRange([
      {
        ...ranges.selection,
        endDate: endDate < minEndDate ? minEndDate : endDate,
      },
    ]);
  };

  // Toggle date picker
  const toggleDatePicker = () => {
    setShowDatePicker((prev) => !prev);
  };

  // Handle adult increment
  const handleAdultIncrement = () => {
    if (adults + children + 1 > maxGuests) {
      toast.error(`Maximum ${maxGuests} guests allowed for this villa`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setAdults(adults + 1);
  };

  // Handle adult decrement
  const handleAdultDecrement = () => {
    if (adults <= 1) {
      toast.error('At least one adult is required', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setAdults(adults - 1);
  };

  // Handle children increment
  const handleChildrenIncrement = () => {
    if (adults + children + 1 > maxGuests) {
      toast.error(`Maximum ${maxGuests} guests allowed for this villa`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setChildren(children + 1);
  };

  // Handle children decrement
  const handleChildrenDecrement = () => {
    if (children <= 0) {
      return;
    }
    
    setChildren(children - 1);
  };

  // Handle search button click
  const handleSearch = () => {
    if (!selectedVilla) {
      toast.error('Please select a villa before searching', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const villa = villas.find((v) => v.title === selectedVilla);
    if (villa) {
      const selectedNights = Math.ceil((dateRange[0].endDate - dateRange[0].startDate) / (1000 * 60 * 60 * 24));
      if (selectedNights < 2) {
        toast.error('Minimum stay of 2 nights required', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
      
      navigate(`/villa/${villa.title.toLowerCase().replace(/\s+/g, '-')}`, {
        state: { 
          villa,
          dateRange: dateRange[0],
          adults,
          children
        },
      });
    }
  };

  // Disable blocked dates in calendar
  const disabledDay = (date) => {
    return blockedDates.includes(date.toISOString().split('T')[0]);
  };

  // Use villa images for slider, fallback to provided images if none
  const slides = villas.length > 0 ? villas.map((villa) => ({
    image: villa.imageUrls[0] || 'https://firebasestorage.googleapis.com/v0/b/phone-auth-d0a8d.appspot.com/o/Luxeunbounded%2Fvilla7.webp?alt=media&token=1d367970-1228-46d9-bc5e-b192f5335619',
    title: villa.title,
    description: villa.description,
  })) : [
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/phone-auth-d0a8d.appspot.com/o/Luxeunbounded%2Fvilla7.webp?alt=media&token=1d367970-1228-46d9-bc5e-b192f5335619',
      title: 'Sunny Woodla | Kodaikanal, Tamil Nadu',
      description: 'Sunny Woodla is a century-old 6-bedroom bungalow nestled in the misty hills of Kodaikanal',
    },
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/phone-auth-d0a8d.appspot.com/o/Luxeunbounded%2Fvilla4.webp?alt=media&token=5430db51-b6cb-478b-af6e-83128d06bd75',
      title: 'Misty Heights Villa | Munnar, Kerala',
      description: 'Misty Heights Villa is a serene escape amidst the rolling tea estates of Munnar',
    },
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/phone-auth-d0a8d.appspot.com/o/Luxeunbounded%2Fvilla9.webp?alt=media&token=41541a8d-0dbf-4abf-86f8-284675df65ed',
      title: 'Haiwatha By The Brook | Ooty, Tamil Nadu',
      description: 'Relive the magic of fairy-tale English life at Lovedale in the Nilgiris.',
    },
  ];

  return (
    <div style={styles.sliderContainer}>
      <Navbar />
      <div style={styles.slider}>
        <img src={slides[currentSlide].image} alt={slides[currentSlide].title} style={styles.sliderImage} />
        <div style={{ ...styles.textOverlay, ...(isMobile ? styles.textOverlayMobile : {}) }}>
          <h1 style={{ ...styles.title, ...(isMobile ? styles.titleMobile : {}) }}>{slides[currentSlide].title}</h1>
          <p style={{ ...styles.description, ...(isMobile ? styles.descriptionMobile : {}) }}>{slides[currentSlide].description}</p>
        </div>
      </div>

      <div style={{ ...styles.searchBar, ...(isMobile ? styles.searchBarMobile : {}) }}>
        <p style={{ ...styles.searchText, ...(isMobile ? styles.searchTextMobile : {}) }}>Extraordinary Experiences • Stay with Luxe Unbounded</p>
        <div style={{ ...styles.searchInputs, ...(isMobile ? styles.searchInputsMobile : {}) }}>
          <select
            value={selectedVilla}
            onChange={(e) => setSelectedVilla(e.target.value)}
            style={{ ...styles.input, ...(isMobile ? styles.inputMobile : {}) }}
            className="form-control"
          >
            <option value="">Select Villa</option>
            {villas.map((villa) => (
              <option key={villa.id} value={villa.title}>
                {villa.title}
              </option>
            ))}
          </select>

          <div style={{ ...styles.datePickerWrapper, ...(isMobile ? styles.datePickerWrapperMobile : {}) }} className="date-picker-wrapper">
            <input
              type="text"
              value={`${formatDate(dateRange[0].startDate)} - ${formatDate(dateRange[0].endDate)}`}
              onClick={toggleDatePicker}
              readOnly
              style={{ ...styles.input, ...(isMobile ? styles.inputMobile : {}) }}
              className="form-control"
              placeholder="Check-in - Check-out"
            />
            {showDatePicker && (
              <div style={{ ...styles.datePickerContainer, ...(isMobile ? styles.datePickerContainerMobile : {}) }}>
                <DateRange
                  onChange={handleDateChange}
                  moveRangeOnFirstSelection={false}
                  ranges={dateRange}
                  direction="horizontal"
                  months={isMobile ? 1 : 2}
                  preventSnapRefocus={true}
                  calendarFocus="forwards"
                  locale={enUS}
                  showMonthAndYearPickers={true}
                  showDateDisplay={false}
                  minDate={today}
                  disabledDay={disabledDay}
                />
              </div>
            )}
          </div>

          <div style={{ ...styles.guestsInput, ...(isMobile ? styles.guestsInputMobile : {}) }}>
            <span style={{ ...styles.guestsLabel, ...(isMobile ? styles.guestsLabelMobile : {}) }}>Adults</span>
            <button
              onClick={handleAdultDecrement}
              style={{ ...styles.guestBtn, ...(isMobile ? styles.guestBtnMobile : {}) }}
              disabled={adults <= 1}
            >
              −
            </button>
            <span style={{ ...styles.guestsText, ...(isMobile ? styles.guestsTextMobile : {}) }}>
              {adults}
            </span>
            <button
              onClick={handleAdultIncrement}
              style={{ ...styles.guestBtn, ...(isMobile ? styles.guestBtnMobile : {}) }}
            >
              +
            </button>
          </div>

          <div style={{ ...styles.guestsInput, ...(isMobile ? styles.guestsInputMobile : {}) }}>
            <span style={{ ...styles.guestsLabel, ...(isMobile ? styles.guestsLabelMobile : {}) }}>Children</span>
            <button
              onClick={handleChildrenDecrement}
              style={{ ...styles.guestBtn, ...(isMobile ? styles.guestBtnMobile : {}) }}
              disabled={children <= 0}
            >
              −
            </button>
            <span style={{ ...styles.guestsText, ...(isMobile ? styles.guestsTextMobile : {}) }}>
              {children}
            </span>
            <button
              onClick={handleChildrenIncrement}
              style={{ ...styles.guestBtn, ...(isMobile ? styles.guestBtnMobile : {}) }}
            >
              +
            </button>
          </div>

          <button style={{ ...styles.searchBtn, ...(isMobile ? styles.searchBtnMobile : {}) }} onClick={handleSearch}>
            SEARCH
          </button>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

const styles = {
  sliderContainer: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    fontFamily: "Josefin Sans" ,
  },
  slider: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  sliderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'brightness(0.65)',
    transition: 'opacity 0.5s ease-in-out',
  },
  textOverlay: {
    position: 'absolute',
    top: '25%',
    left: '60px',
    color: '#fff',
    maxWidth: '700px',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  },
  textOverlayMobile: {
    top: '15%',
    left: '20px',
    maxWidth: '90%',
  },
  title: {
    fontSize: '56px',
    fontWeight: '700',
    marginBottom: '15px',
    letterSpacing: '1px',
    fontFamily: "Josefin Sans" ,
  },
  titleMobile: {
    fontSize: '28px',
    marginBottom: '10px',
  },
  description: {
    fontSize: '20px',
    lineHeight: 1.6,
    fontWeight: '300',
  },
  descriptionMobile: {
    fontSize: '14px',
    lineHeight: 1.4,
  },
  searchBar: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    right: '0px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '20px',
    borderRadius: '0px',
    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    transition: 'transform 0.3s ease',
    fontFamily: "Josefin Sans" ,
  },
  searchBarMobile: {
    padding: '15px',
  },
  searchText: {
    fontSize: '20px',
    fontWeight: '600',
    fontStyle: 'italic',
    color: '#8e3b6b',
    fontFamily: "Josefin Sans" ,
    margin: '0',
    textAlign: 'center',
  },
  searchTextMobile: {
    fontSize: '14px',
  },
  searchInputs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    alignItems: 'center',
    fontFamily: "Josefin Sans" ,
  },
  searchInputsMobile: {
    gap: '10px',
    flexDirection: 'column',
  },
  input: {
    flex: '1 1 0',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '16px',
    minWidth: '200px',
    transition: 'border-color 0.3s ease',
    fontFamily: "Josefin Sans" ,
  },
  inputMobile: {
    width: '100%',
    minWidth: '100%',
    fontSize: '14px',
    padding: '10px',
  },
  datePickerWrapper: {
    position: 'relative',
    flex: '1 1 0',
    minWidth: '200px',
  },
  datePickerWrapperMobile: {
    width: '100%',
    minWidth: '100%',
  },
  datePickerContainer: {
    position: 'absolute',
    zIndex: 998,
    bottom: '100%',
    left: '0',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    marginBottom: '5px',
  },
  datePickerContainerMobile: {
    width: '100%',
    left: '0',
    top: 'auto',
    bottom: '100%',
  },
  searchBtn: {
    flex: '1 1 0',
    backgroundColor: '#8e3b6b',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '16px',
    textTransform: 'uppercase',
    transition: 'background-color 0.3s ease',
    fontFamily: "Josefin Sans" ,
  },
  searchBtnMobile: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
  },
  guestsInput: {
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '8px 12px',
    minWidth: '200px',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    transition: 'border-color 0.3s ease',
    fontFamily: "Josefin Sans" ,
  },
  guestsInputMobile: {
    width: '100%',
    minWidth: '100%',
    padding: '8px 10px',
    justifyContent: 'space-between',
    gap: '12px',
  },
  guestBtn: {
    background: '#8e3b6b',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '6px 0',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'background-color 0.3s ease',
    width: '32px',
    textAlign: 'center',
    fontFamily: "Josefin Sans" ,
  },
  guestBtnMobile: {
    padding: '6px 0',
    fontSize: '16px',
    width: '30px',
  },
  guestsLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    width: '60px',
    textAlign: 'left',
    fontFamily: "Josefin Sans" ,
  },
  guestsLabelMobile: {
    fontSize: '14px',
    width: '55px',
  },
  guestsText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    width: '40px',
    textAlign: 'center',
    fontFamily: "Josefin Sans",
  },
  guestsTextMobile: {
    fontSize: '14px',
    width: '35px', 
  },
};

export default ImageSlider;